import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Star as PrincipalIcon,
  MedicalServices as DiagnosisIcon
} from '@mui/icons-material';
import { debounce } from 'lodash';
import { searchProcedureCodesByType } from '../../services/procedureCodesService';

/**
 * DiagnosisCodeSelector - Industry-standard ICD-10 diagnosis code selector
 * 
 * Features:
 * - ICD-10 code search with autocomplete
 * - Multiple diagnosis selection (up to 12 per industry standard)
 * - Reordering support (first position = principal diagnosis)
 * - Diagnosis type assignment (principal, secondary, admitting)
 * - Visual indicators for diagnosis sequence
 * 
 * @param {Array} value - Array of EncounterDiagnosis objects
 * @param {Function} onChange - Callback when diagnoses change
 * @param {boolean} disabled - Whether the component is disabled
 * @param {boolean} required - Whether at least one diagnosis is required
 * @param {string} encounterType - Type of encounter (affects available diagnosis types)
 * @param {number} maxDiagnoses - Maximum number of diagnoses (default: 12)
 */
const DiagnosisCodeSelector = ({
  value = [],
  onChange,
  disabled = false,
  required = false,
  encounterType = 'outpatient',
  maxDiagnoses = 12,
  error = false
}) => {
  const [searchOptions, setSearchOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Use ref to track latest value to avoid stale closures in debounced function
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Get available diagnosis types based on encounter type
  const getDiagnosisTypes = () => {
    const baseTypes = [
      { value: 'principal', label: 'Principal Diagnosis' },
      { value: 'secondary', label: 'Secondary Diagnosis' },
      { value: 'other', label: 'Other Diagnosis' }
    ];
    
    // Add admitting diagnosis for inpatient encounters
    if (encounterType === 'inpatient' || encounterType === 'inpatient_admission') {
      baseTypes.splice(1, 0, { value: 'admitting', label: 'Admitting Diagnosis' });
    }
    
    return baseTypes;
  };

  // Debounced search function for ICD-10 codes
  const debouncedSearch = useMemo(
    () => debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const results = await searchProcedureCodesByType(
          'ICD-10',
          searchTerm,
          true // activeOnly
        );
        
        // Filter out already selected codes and limit results
        const selectedCodes = valueRef.current.map(d => d.icdCode);
        const filteredResults = results
          .filter(r => !selectedCodes.includes(r.procedure_code))
          .slice(0, 20);
        
        setSearchOptions(filteredResults);
      } catch (error) {
        console.error('Error searching ICD-10 codes:', error);
        setSearchOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [] // Empty deps - uses ref for value
  );

  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Add a new diagnosis
  const handleAddDiagnosis = (event, selectedCode) => {
    if (!selectedCode) return;

    const newSequence = value.length + 1;
    const newDiagnosis = {
      id: `diag_${Date.now()}_${newSequence}`,
      icdCode: selectedCode.procedure_code,
      icdDescription: selectedCode.name,
      sequence: newSequence,
      type: newSequence === 1 ? 'principal' : 'secondary',
      diagnosedDate: new Date(),
      isActive: true
    };

    onChange([...value, newDiagnosis]);
    setInputValue('');
  };

  // Remove a diagnosis
  const handleRemoveDiagnosis = (diagnosisId) => {
    const filtered = value.filter(d => d.id !== diagnosisId);
    // Resequence remaining diagnoses
    const resequenced = filtered.map((d, index) => ({
      ...d,
      sequence: index + 1,
      // First diagnosis becomes principal if removing the principal one
      type: index === 0 && d.type !== 'admitting' ? 'principal' : d.type === 'principal' ? 'secondary' : d.type
    }));
    onChange(resequenced);
  };

  // Move diagnosis up in sequence
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newValue = [...value];
    [newValue[index - 1], newValue[index]] = [newValue[index], newValue[index - 1]];
    // Update sequences and types
    const resequenced = newValue.map((d, i) => ({
      ...d,
      sequence: i + 1,
      type: i === 0 && d.type !== 'admitting' ? 'principal' : d.type === 'principal' && i > 0 ? 'secondary' : d.type
    }));
    onChange(resequenced);
  };

  // Move diagnosis down in sequence
  const handleMoveDown = (index) => {
    if (index === value.length - 1) return;
    const newValue = [...value];
    [newValue[index], newValue[index + 1]] = [newValue[index + 1], newValue[index]];
    // Update sequences and types
    const resequenced = newValue.map((d, i) => ({
      ...d,
      sequence: i + 1,
      type: i === 0 && d.type !== 'admitting' ? 'principal' : d.type === 'principal' && i > 0 ? 'secondary' : d.type
    }));
    onChange(resequenced);
  };

  // Update diagnosis type
  const handleTypeChange = (diagnosisId, newType) => {
    const updated = value.map(d => {
      if (d.id === diagnosisId) {
        return { ...d, type: newType };
      }
      // If setting a new principal, demote the old principal
      if (newType === 'principal' && d.type === 'principal' && d.id !== diagnosisId) {
        return { ...d, type: 'secondary' };
      }
      return d;
    });
    onChange(updated);
  };

  // Get chip color based on diagnosis type
  const getTypeColor = (type) => {
    switch (type) {
      case 'principal': return 'primary';
      case 'admitting': return 'warning';
      case 'secondary': return 'default';
      default: return 'default';
    }
  };

  const hasPrincipal = value.some(d => d.type === 'principal');

  return (
    <Box>
      {/* Search/Add Diagnosis */}
      <Autocomplete
        value={null}
        onChange={handleAddDiagnosis}
        inputValue={inputValue}
        onInputChange={(event, newValue) => setInputValue(newValue)}
        options={searchOptions}
        loading={loading}
        disabled={disabled || value.length >= maxDiagnoses}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return `${option.procedure_code} - ${option.name}`;
        }}
        renderOption={(props, option) => {
          const { key, ...restProps } = props;
          return (
            <Box component="li" key={key} {...restProps}>
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label="ICD-10" 
                    size="small" 
                    variant="outlined"
                    color="info"
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.procedure_code}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {option.name}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add Diagnosis Code (ICD-10)"
            placeholder="Search by code or description..."
            required={required && value.length === 0}
            error={error}
            size="small"
            sx={{
              '& .MuiInputLabel-root': {
                fontFamily: 'Roboto, Arial, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
              },
              '& .MuiInputBase-input': {
                fontFamily: 'Roboto, Arial, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
              },
              '& .MuiInputBase-input::placeholder': {
                fontFamily: 'Roboto, Arial, sans-serif',
                fontWeight: 400,
                fontSize: '0.875rem',
                opacity: 0.7,
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <DiagnosisIcon sx={{ color: 'action.active', mr: 1 }} />
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        PaperComponent={({ children, ...props }) => (
          <Paper {...props} sx={{ maxHeight: 350 }}>
            {children}
          </Paper>
        )}
        noOptionsText={
          inputValue.length < 2 
            ? "Type 2+ characters to search ICD-10 codes..." 
            : "No matching diagnosis codes found"
        }
        loadingText="Searching ICD-10 codes..."
        clearOnBlur
        selectOnFocus
        blurOnSelect
      />

      {/* Validation Warning */}
      {value.length > 0 && !hasPrincipal && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          A principal diagnosis is required. The first diagnosis in the list should be marked as Principal.
        </Alert>
      )}

      {/* Selected Diagnoses List */}
      {value.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Selected Diagnoses ({value.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Drag to reorder. First position = Principal diagnosis for billing.
            </Typography>
          </Box>
          <List disablePadding>
            {value.map((diagnosis, index) => (
              <React.Fragment key={diagnosis.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    py: 1,
                    px: 1.5,
                    bgcolor: diagnosis.type === 'principal' ? 'primary.50' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                  secondaryAction={null}
                >
                  {/* Sequence Number */}
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: diagnosis.type === 'principal' ? 'primary.main' : 'grey.400',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      flexShrink: 0
                    }}
                  >
                    {diagnosis.sequence}
                  </Box>

                  {/* Diagnosis Info */}
                  <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ fontWeight: 600 }}
                      >
                        {diagnosis.icdCode}
                      </Typography>
                      {diagnosis.type === 'principal' && (
                        <Tooltip title="Principal Diagnosis">
                          <PrincipalIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {diagnosis.icdDescription}
                    </Typography>
                  </Box>

                  {/* Type Selector */}
                  <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                    <Select
                      value={diagnosis.type}
                      onChange={(e) => handleTypeChange(diagnosis.id, e.target.value)}
                      disabled={disabled}
                      sx={{ 
                        '& .MuiSelect-select': { py: 0.5 }
                      }}
                    >
                      {getDiagnosisTypes().map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          <Chip 
                            label={type.label} 
                            size="small" 
                            color={getTypeColor(type.value)}
                            sx={{ fontSize: '0.75rem', height: 22 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Action Buttons - Up/Down arrows then Delete */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: 1 }}>
                    <Tooltip title="Move Up">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(index)}
                          disabled={disabled || index === 0}
                          sx={{ p: 0.5 }}
                        >
                          <ArrowUpIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move Down">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(index)}
                          disabled={disabled || index === value.length - 1}
                          sx={{ p: 0.5 }}
                        >
                          <ArrowDownIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveDiagnosis(diagnosis.id)}
                        disabled={disabled}
                        color="error"
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

    </Box>
  );
};

export default DiagnosisCodeSelector;
